import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { getInitData } from '@shared/lib/telegram';

export interface MoneyDto {
  amount: string;
  label: string;
}

const authenticatedQuery = fetchBaseQuery({
    baseUrl: '/api',
    credentials: 'same-origin',
    prepareHeaders: (headers) => {
      // Re-read on every request: Telegram can refresh initData mid-session.
      const initData = getInitData();
      if (initData) headers.set('Authorization', `tma ${initData}`);
      const csrf = document.cookie.split('; ').find((part) => part.startsWith('ys_csrf='))?.slice(8);
      if (csrf) headers.set('X-CSRF-Token', csrf);
      headers.set('X-YeneShop-Request', '1');
      return headers;
    },
  });

const baseQuery: typeof authenticatedQuery = async (args, api, options) => {
    const result = await authenticatedQuery(args, api, options);
    if (result.error?.status === 401 && api.type === 'mutation' && !['startSignIn', 'signOut'].includes(api.endpoint)) window.dispatchEvent(new Event('yeneshop:sign-in-required'));
    return result;
  };

/**
 * One API slice for the whole app; entity slices extend it with injectEndpoints
 * so each business domain owns its own queries without a second cache.
 */
export const baseApi = createApi({
  reducerPath: 'api',
  tagTypes: [
    'Notification',
    'User',
    'Account',
    'SellerShop',
    'Product',
    'Order',
    'Deposit',
    'Admin',
    'AdminSummary',
    'AdminSupplier',
    'AdminAnalytics',
    'AdminDeposit',
    'AdminProduct',
    'ManualProduct',
    'Delivery',
    'CanvaOrder',
    'AdminOrder',
    'AdminUser',
    'Ban',
    'Discount',
    'Maintenance',
    'Feedback',
    'AdminFeedback',
    'ResellerAccount',
    'ResellerProduct',
    'ResellerOrder',
    'AdminReseller',
    'AdminResellerProduct',
    'Backup',
    'Giveaway',
    'AdminGiveaway',
    'Category',
    'AdminCategory',
    'PinnedNumber',
    'SmsPricing',
    'NumbersVisibility',
    'NumberService',
    'ShopService',
    'AdminShopService',
    'Celebration',
    'FlashDraw',
    'AdminFlashDraw',
  ],
  baseQuery,
  endpoints: () => ({}),
});

/** Pulls a readable message out of an RTK Query error shape. */
export function apiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === 'object' && data !== null && 'message' in data) {
      const message = (data as { message?: unknown }).message;
      if (typeof message === 'string') return message;
    }
  }

  return fallback;
}

/** Returns the stable server error code from an RTK Query error, when present. */
export function apiErrorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null || !('data' in error)) return null;
  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('error' in data)) return null;
  const code = (data as { error?: unknown }).error;
  return typeof code === 'string' ? code : null;
}
